import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { subscribeToEvent, subscribeToDisciplines, subscribeToEventBouts } from "../firebase/services";
import { formatDateRange, formatDayTime } from "../utils/formatDateRange";
import { calculateSweepstakes } from "../utils/sweepstakes";
import SweepstakesResults, { SweepstakesInfo } from "../components/Sweepstakes";
import { sanitizeHtml, toEditableHtml, isHtmlEmpty } from "../utils/sanitizeHtml";
import logo from "../assets/logo.png";

export default function PublicEvent() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [bouts, setBouts] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "home";
  const setActiveTab = (tab) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("tab", tab);
    return next;
  }, { replace: true });

  useEffect(() => {
    const unsub1 = subscribeToEvent(eventId, setEvent);
    const unsub2 = subscribeToDisciplines(eventId, setDisciplines);
    const unsub3 = subscribeToEventBouts(eventId, setBouts);
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [eventId]);

  const sweepstakes = useMemo(
    () => calculateSweepstakes(disciplines, bouts, event?.fighterAffiliations || {}),
    [disciplines, bouts, event?.fighterAffiliations]
  );

  // Only disciplines with at least one schedule field set, earliest first (check-in time
  // if given, else pool start) — disciplines nobody's scheduled yet just don't show up
  // rather than cluttering the tab with empty cards.
  const scheduleRows = useMemo(() => {
    const schedules = event?.disciplineSchedules || {};
    return disciplines
      .filter(d => {
        const s = schedules[d.id];
        return s && (s.eventDate || s.checkInTime || s.poolStartTime);
      })
      .map(d => ({ disc: d, sched: schedules[d.id] }))
      .sort((a, b) => {
        const key = (s) => `${s.eventDate || "9999"} ${s.checkInTime || s.poolStartTime || "99:99"}`;
        return key(a.sched).localeCompare(key(b.sched)) || a.disc.name.localeCompare(b.disc.name);
      });
  }, [disciplines, event?.disciplineSchedules]);

  if (!event) return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading...</div>;

  const isTournament = event.type === "tournament";
  const typeLabels = { tournament: "Tournament", seminar: "Judging Seminar", training: "Training" };

  return (
    <div className="min-h-screen p-5">
      <Link to="/" className="text-gray-600 text-sm no-underline hover:text-gray-400">← Home</Link>

      <div className="text-center my-4">
        <img src={logo} alt="Joy of HEMA" className="w-80 mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-sm font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
      </div>

      <h1 className="font-display text-xl font-bold mt-3 mb-1">{event.name}</h1>
      <div className="text-gray-600 text-xs mb-4">
        {typeLabels[event.type] || event.type}
        {event.startDate && <span> • {formatDateRange(event.startDate, event.endDate)}</span>}
        {event.location && <span> • {event.location}</span>}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-5 border-b border-dark-500 overflow-x-auto">
        {[
          { key: "home", label: "HOME" },
          ...(isTournament ? [{ key: "schedule", label: "SCHEDULE" }] : []),
          { key: "disciplines", label: isTournament ? "DISCIPLINES" : "EVENT" },
          ...(isTournament ? [{ key: "sweeps-info", label: "SWEEPS INFO" }] : []),
          ...(isTournament && event.sweepsVisible ? [{ key: "sweeps-results", label: "SWEEPS RESULTS" }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} aria-pressed={activeTab === t.key}
            className="px-4 py-2.5 font-display text-xs font-bold tracking-widest whitespace-nowrap border-b-2 bg-transparent cursor-pointer"
            style={{ color: activeTab === t.key ? "#fff" : "#bbb", borderColor: activeTab === t.key ? "#81C784" : "transparent" }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "home" && (
        isHtmlEmpty(event.welcomeContent) ? (
          <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 text-sm text-gray-300">
            Welcome to {event.name}!
          </div>
        ) : (
          <div className="rich-content bg-dark-700 border border-dark-500 rounded-xl p-4 text-sm text-gray-300"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(toEditableHtml(event.welcomeContent)) }} />
        )
      )}

      {activeTab === "schedule" && isTournament && (
        <div>
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">SCHEDULE</div>
          {scheduleRows.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-4">No schedule posted yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {scheduleRows.map(({ disc, sched }) => (
                <div key={disc.id} className="bg-dark-700 border border-dark-500 rounded-xl p-4">
                  <div className="font-display text-base font-bold text-white mb-1">{disc.name}</div>
                  {sched.eventDate && (
                    <div className="text-sm text-gray-400 mb-2">{formatDayTime(sched.eventDate, "")}</div>
                  )}
                  {sched.checkInTime && (
                    <div className="text-sm text-gray-300 mb-1">
                      <span className="text-gray-600">Check-in / Gear Check: </span>
                      {formatDayTime("", sched.checkInTime)}
                    </div>
                  )}
                  {sched.poolStartTime && (
                    <div className="text-sm text-gray-300">
                      <span className="text-gray-600">Pool Start: </span>
                      {formatDayTime("", sched.poolStartTime)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "sweeps-info" && isTournament && <SweepstakesInfo />}

      {activeTab === "sweeps-results" && isTournament && event.sweepsVisible && (
        <SweepstakesResults schools={sweepstakes.schools} />
      )}

      {activeTab === "disciplines" && (isTournament ? (
        <div>
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
            DISCIPLINES ({disciplines.length})
          </div>
          <div className="flex flex-col gap-2">
            {disciplines.sort((a, b) => a.name.localeCompare(b.name)).map(disc => {
              const discBouts = bouts.filter(b => b.disciplineId === disc.id);
              const completed = discBouts.filter(b => b.status === "confirmed").length;
              return (
                <button key={disc.id} onClick={() => navigate(`/public/${eventId}/discipline/${disc.id}`)}
                  className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 cursor-pointer hover:border-gray-600 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display text-base font-bold text-white">{disc.name}</div>
                      <div className="text-gray-600 text-xs mt-1">
                        {disc.fighters?.length || 0} fighters • {disc.poolCount ?? disc.pools?.length ?? 0} pools • {discBouts.length} bouts
                      </div>
                    </div>
                    <div className="text-right">
                      {discBouts.length > 0 && (
            <div className="text-xs font-bold" style={{ color: completed === discBouts.length && discBouts.length > 0 ? "#81C784" : "#bbb" }}>
                          {completed}/{discBouts.length}
                        </div>
                      )}
                      <div className="text-gray-700 text-lg">→</div>
                    </div>
                  </div>
                </button>
              );
            })}
            {disciplines.length === 0 && (
              <p className="text-gray-600 text-sm text-center py-4">No disciplines yet.</p>
            )}
          </div>
        </div>
      ) : (
        <div>
          {/* Seminar/Training: show bouts list */}
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
            BOUTS ({bouts.length})
          </div>
          {bouts.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-4">No bouts yet.</p>
          ) : (
            bouts.map(bout => {
              const vis = { scores: true, standings: true, judgeBreakdown: false };
              return (
                <div key={bout.id} className="bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-sm font-bold" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</span>
                      <span className="text-gray-600 text-xs">vs</span>
                      <span className="font-display text-sm font-bold" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</span>
                    </div>
            <span className="font-display text-xs font-bold" style={{ color: bout.status === "confirmed" ? "#81C784" : "#bbb" }}>
                      {bout.status === "confirmed" ? "✓ Done" : bout.status === "scoring" ? "In Progress" : "Upcoming"}
                    </span>
                  </div>
                  {vis.scores && bout.confirmedResult && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                      <span className="font-display font-bold" style={{ color: "#1E88E5" }}>{bout.confirmedResult.scoreB}</span>
                      <span>–</span>
                      <span className="font-display font-bold" style={{ color: "#E53935" }}>{bout.confirmedResult.scoreA}</span>
                      {bout.confirmedResult.winner && bout.confirmedResult.winner !== "Draw" && (
                        <span className="ml-2 text-gray-600">Winner: {bout.confirmedResult.winner}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      ))}
    </div>
  );
}
